import {computed, DestroyRef, inject, Injectable, signal} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {FormGroup} from '@angular/forms';
import {UserModel, UserPreferDateFormatType} from '@core/api/data-access';
import {CommunicationPreference} from '../models/user-form.model';
import {IUserCommunicationPreference, IUserFormStruct, UserFormSection} from '../models/user-form.types';

// Interface for field change
export interface FieldChange {
	field: string;
	originalValue: string | number | boolean | null;
	newValue: string | number | boolean | null;
	section?: UserFormSection;
}

@Injectable({providedIn: 'root'})
export class UserFormChangesService {
	private readonly destroyRef = inject(DestroyRef);
	// Original user model (from which changes are tracked)
	private readonly originalUser = signal<UserModel | null>(null);

	// Form reference
	private formGroup: FormGroup | null = null;
	private sections: UserFormSection[] | null = null;

	// UI state
	public readonly isExpanded = signal<boolean>(false);

	// Computed fields
	public readonly fieldChanges = signal<FieldChange[]>([]);
	public readonly hasChanges = computed(() => this.fieldChanges().length > 0);

	// Initialize tracking
	public initializeChangeTracking(originalUser: UserModel | null, formGroup: FormGroup, sections: UserFormSection[]): void {
		if (!originalUser) {
			// אם מדובר ביצירת משתמש חדש, אין צורך לעקוב אחר שינויים
			return;
		}

		this.isExpanded.set(false);
		this.originalUser.set(originalUser);
		this.formGroup = formGroup;
		this.sections = sections;
		// Subscribe to form value changes to track differences
		this.formGroup.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
			this.detectChanges();
		});

		// Run initial detection
		this.detectChanges();
	}

	// Toggle expanded state of the changes panel
	public toggleExpanded(): void {
		this.isExpanded.update(val => !val);
	}

	// Clear all tracked changes
	public resetChanges(): void {
		this.fieldChanges.set([]);
	}

	// Main method to detect changes between original user and current form values
	private detectChanges(): void {
		if (!this.formGroup || !this.originalUser()) {
			return;
		}

		const formValues = this.formGroup.getRawValue();
		const originalUser = this.originalUser();
		const changes: FieldChange[] = [];

		// Check general fields
		if (this.sections?.includes('GeneralDetails')) {
			this.checkGeneralFields(formValues, originalUser, changes);
		}

		// Check contact info
		if (this.sections?.includes('ContactInfo')) {
			this.checkContactInfo(formValues, originalUser, changes);
		}

		// Check password
		if (this.sections?.includes('Password')) {
			if (this.isDifferent('', formValues.password)) {
				changes.push({
					field: 'password',
					originalValue: this.formatValue(''),
					newValue: this.formatValue(formValues.password),
					section: 'Password',
				});
			}
		}

		// Check permissions
		if (this.sections?.includes('Permissions')) {
			this.checkPermissions(formValues, originalUser, changes);
		}

		// Check user preferences
		if (this.sections?.includes('ActiveUserPreferences')) {
			this.checkUserPreferences(formValues, originalUser, changes);
		}

		// Update changes
		this.fieldChanges.set(changes);
	}

	// Check general fields for changes
	private checkGeneralFields(formValues: Partial<IUserFormStruct>, originalUser: UserModel, changes: FieldChange[]): void {
		// שדות כלליים
		const generalFields: (keyof UserModel)[] = ['username', 'fullname', 'title', 'company', 'department', 'notes'];

		generalFields.forEach(field => {
			const originalValue = originalUser[field];
			const newValue = formValues[field];

			if (this.isDifferent(originalValue, newValue)) {
				changes.push({
					field,
					originalValue: this.formatValue(originalValue),
					newValue: this.formatValue(newValue),
					section: 'GeneralDetails',
				});
			}
		});
	}

	// Check contact info for changes
	private checkContactInfo(formValues: Partial<IUserFormStruct>, originalUser: UserModel, changes: FieldChange[]): void {
		// בדיקת שדות קשר
		const contactFields: (keyof UserModel)[] = ['email', 'phonenumber'];

		contactFields.forEach(field => {
			const originalValue = originalUser[field];
			const newValue = formValues[field];

			if (this.isDifferent(originalValue, newValue)) {
				changes.push({
					field,
					originalValue: this.formatValue(originalValue),
					newValue: this.formatValue(newValue),
					section: 'ContactInfo',
				});
			}
		});

		// בדיקת העדפות תקשורת
		let originalCommunicationPref: IUserCommunicationPreference = null;
		if (originalUser.preferences.notifyEmail) {
			originalCommunicationPref = CommunicationPreference.notifyEmail;
		} else if (originalUser.preferences.notifySMS) {
			originalCommunicationPref = CommunicationPreference.notifySMS;
		} else if (originalUser.preferences.notifyWhatsapp) {
			originalCommunicationPref = CommunicationPreference.notifyWhatsapp;
		} else {
			originalCommunicationPref = CommunicationPreference.none;
		}

		const newCommunicationPref = formValues.communicationPreference;

		if (this.isDifferent(originalCommunicationPref, newCommunicationPref)) {
			changes.push({
				field: 'communicationPreference.label',
				originalValue: this.formatCommunicationPref(originalCommunicationPref),
				newValue: this.formatCommunicationPref(newCommunicationPref),
				section: 'ContactInfo',
			});
		}
	}

	// Check permissions for changes
	private checkPermissions(formValues: Partial<IUserFormStruct>, originalUser: UserModel, changes: FieldChange[]): void {
		// בדיקת הרשאות
		if (!formValues.permissions) {
			return;
		}

		const originalRoles = originalUser.roles;
		const newRoles = Object.keys(formValues.permissions).filter(role => formValues.permissions[role]);

		// Check if roles are different
		const rolesChanged = originalRoles.length !== newRoles.length || !originalRoles.every(role => newRoles.includes(role));

		if (rolesChanged) {
			changes.push({
				field: 'roles',
				originalValue: originalRoles.join(', '),
				newValue: newRoles.join(', '),
				section: 'Permissions',
			});
		}
	}

	// Check user preferences for changes
	private checkUserPreferences(formValues: Partial<IUserFormStruct>, originalUser: UserModel, changes: FieldChange[]): void {
		// בדיקת העדפות משתמש
		const preferencesFields: {field: string; modelPath: string}[] = [
			{field: 'dateFormat', modelPath: 'preferences.dateFormat'},
			{field: 'daysToReminder', modelPath: 'preferences.daysToReminder'},
			{field: 'penColor', modelPath: 'preferences.penColor'},
			{field: 'penWidth', modelPath: 'preferences.penWidth'},
		];

		preferencesFields.forEach(({field, modelPath}) => {
			const path = modelPath.split('.');
			const originalValue = path.reduce((obj, prop) => (obj && obj[prop] !== undefined ? obj[prop] : null), originalUser as any);
			const newValue = formValues[field];

			if (this.isDifferent(originalValue, newValue)) {
				let displayOriginalValue = this.formatValue(originalValue);
				let displayNewValue = this.formatValue(newValue);

				// מטפל בפורמט תאריך בצורה מיוחדת
				if (field === 'dateFormat') {
					displayOriginalValue = this.formatDateFormatType(originalValue as UserPreferDateFormatType);
					displayNewValue = this.formatDateFormatType(newValue as UserPreferDateFormatType);
				}

				changes.push({
					field,
					originalValue: displayOriginalValue,
					newValue: displayNewValue,
					section: 'ActiveUserPreferences',
				});
			}
		});
	}

	// Helper to check if values are different
	private isDifferent(originalValue: any, newValue: any): boolean {
		// Handle null/undefined cases
		if (originalValue === null || originalValue === undefined) {
			return newValue !== null && newValue !== undefined && newValue !== '';
		}

		if (newValue === null || newValue === undefined) {
			return originalValue !== null && originalValue !== undefined && originalValue !== '';
		}

		// Convert to strings for comparison if they are strings/numbers
		if (typeof originalValue === 'string' || typeof originalValue === 'number' || typeof newValue === 'string' || typeof newValue === 'number') {
			return String(originalValue).trim() !== String(newValue).trim();
		}

		// Default comparison
		return originalValue !== newValue;
	}

	// Format value for display
	private formatValue(value: any): string | null {
		if (value === null || value === undefined || value === '') {
			return null;
		}

		if (Array.isArray(value)) {
			return value.join(', ');
		}

		return String(value);
	}

	// Format communication preference for display
	private formatCommunicationPref(pref: IUserCommunicationPreference): string | null {
		if (!pref) pref = 'none';
		const normalizedPref = pref.replace('notify', '')?.toLowerCase();
		return `user.form.communication.${normalizedPref}`;
	}

	// Format date format type for display
	private formatDateFormatType(formatType: UserPreferDateFormatType): string | null {
		if (formatType === null || formatType === undefined) {
			return null;
		}

		return formatType === UserPreferDateFormatType.DDMMYYYY ? 'user.form.field.dateFormat.european' : 'user.form.field.dateFormat.american';
	}
}
